import { useState, useEffect, useCallback } from "react";
import { MailCheck, RotateCw, ShieldCheck, ArrowLeft } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const postJson = async (path, body) => {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
};

export const EmailVerification = ({ submissionId, email, onVerified, onReset }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Detects verification done via the emailed link (possibly in another tab)
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/contact/status/${submissionId}`);
        const data = await res.json();
        if (data.status === 'verified') onVerified();
      } catch { /* retry on next tick */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [submissionId, onVerified]);

  const handleVerify = useCallback(async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setInfo('');
    try {
      const { ok, data } = await postJson('/api/contact/verify', { submission_id: submissionId, code });
      if (ok && data.success) onVerified();
      else setError(data.detail || 'Verification failed. Please try again.');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }, [code, submissionId, onVerified]);

  const handleResend = useCallback(async () => {
    setError(''); setInfo('');
    try {
      const { ok, data } = await postJson('/api/contact/resend', { submission_id: submissionId });
      if (ok) { setInfo(data.message); setCooldown(60); setCode(''); }
      else setError(data.detail || 'Could not resend the code.');
    } catch {
      setError('Network error. Please try again.');
    }
  }, [submissionId]);

  return (
    <div data-testid="email-verification-step">
      <div className="flex items-center gap-3 mb-4">
        <div className="icon-container flex-shrink-0"><MailCheck className="text-[#C9A962]" size={20} /></div>
        <h3 className="text-2xl font-bold">Verify Your Email</h3>
      </div>
      <p className="text-[#A0ABC0] mb-6" data-testid="verification-sent-text">
        We've sent a 6-digit code to <span className="text-white font-medium">{email}</span>. Enter it below or click the link in that email. Your message will only be submitted after verification.
      </p>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="verification-error">{error}</div>}
      {info && <div className="mb-4 p-3 bg-[#C9A962]/10 border border-[#C9A962]/30 rounded-lg text-[#C9A962] text-sm" data-testid="verification-info">{info}</div>}

      <form onSubmit={handleVerify} className="space-y-5">
        <div>
          <label>Verification Code <span className="text-[#C9A962]">*</span></label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            className="text-center text-2xl tracking-[0.6em] font-mono max-w-[280px]"
            data-testid="verification-code-input"
            required
          />
        </div>
        <button
          type="submit"
          disabled={busy || code.length !== 6}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="verification-submit"
        >
          {busy ? 'Verifying...' : <>Verify &amp; Submit <ShieldCheck size={18} /></>}
        </button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6 text-sm">
        <button type="button" onClick={onReset} className="text-[#A0ABC0] hover:text-[#C9A962] inline-flex items-center gap-1.5 transition-colors" data-testid="verification-change-details">
          <ArrowLeft size={14} /> Edit details
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={cooldown > 0}
          className="text-[#C9A962] hover:underline inline-flex items-center gap-1.5 disabled:text-[#5A6578] disabled:no-underline disabled:cursor-not-allowed"
          data-testid="verification-resend"
        >
          <RotateCw size={14} /> {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
        </button>
      </div>
      <p className="text-[#5A6578] text-xs mt-4">The code expires in 15 minutes. Check your spam folder if you don't see it.</p>
    </div>
  );
};
